import api from './api';

/**
 * Invoice List (Accounting) — one invoice, or one masterpro's worth of orders waiting to
 * become one. Backed by app/api/api_v1/endpoints/invoice_list.py (GET, read-only).
 *
 * Ports V1's `lib_invoice.pl::listinvoice`.
 *
 * THE ROUTE IS A FILTER TUPLE, NOT AN ID. Callers link with whatever identity they have
 * — an invoice_history recno from the EDI screen, a masterpro from a pro screen, a
 * bill-to name from a search box — and the server works out which invoice was meant. It
 * can legitimately answer "several matched" (`ambiguous`), which is a picker, not an
 * error. See `InvoiceListParams`.
 */

/** Every parameter is optional; send only what you have. Names are V1's. */
export interface InvoiceListParams {
  /** An existing invoice, by invoice_history.recno. The most specific filter. */
  invhistoryrecno?: number;
  /** The order's masterpro. Resolves before the invoice exists. */
  masterpro?: string;
  billto?: number;
  /** Bill-to name PREFIX — the server searches `cname LIKE 'X%'`, not a contains match. */
  billtoname?: string;
  /** Invoice number; a trailing letter or two is the accessorial type. */
  invoicenum?: string;
  /** Comma-separated `invoice.recno` selection. */
  invoicerecnos?: string;
  /** ISO or MM/DD/YYYY. Narrows a bill-to-name search only. */
  invoicestartdate?: string;
  invoiceenddate?: string;
}

export type InvoiceListStatus = 'resolved' | 'ambiguous' | 'storage' | 'empty';

export interface InvoiceListLineItem {
  linumber: number;
  liitem: string;
  limastype: string;
  liqty: number;
  lirate: number;
  liratetype: string;
  lidescription: string;
  litotal: number;
}

/** One leg of the move, as the invoice screen shows it. */
export interface InvoiceListLeg {
  legnum: number;
  /** V1's two-letter code for what happened here — PU, To, Rt, BT, CS … */
  code: string;
  location: string;
  drop: string;
  mule: string;
  driver: string;
  date: string | null;
  /** Banded, and shown with its full address — it is the leg being billed for. */
  isDelivery: boolean;
  address: string;
  city: string;
  stateZip: string;
  /** Leg 1 only: the leg's TYPE and its ORIGIN, so the strip starts at the pickup. */
  originCode: string;
  originName: string;
  /** Leg 1 only: when the driver reached the pickup, so that row carries a date too. */
  originDate: string | null;
}

/** One Y/N order flag. `emphasis` is V1's rule, decided server-side, not a style choice:
 *  `alert` is COD (which V1 also forces on when the bill-to's terms are C.O.D.), `flag`
 *  is any other flag set to Y. */
export interface InvoiceListFlag {
  label: string;
  value: string;
  emphasis: 'none' | 'flag' | 'alert';
}

/** One row of the Accessorials strip (V1 lib_invoice.pl:1199). */
export interface InvoiceListAccessorial {
  pronumber: number;
  accessorialnum: number;
  /** cms.invoice.recno — what AccessorialEditModal is addressed by. */
  invoicerecno: number;
  billtoname: string;
  /** Invoice total less credit, V1's link text. */
  netTotal: number;
  /** open | invoiced | exported — V1's uncoloured / red / black rows. */
  state: 'open' | 'invoiced' | 'exported';
  invoicedate: string | null;
  masexportdate: string | null;
}

/** One ADDITIONAL (non-LEG) driver pay item — the Addl Cost tab's lower table. Rate,
 *  commission flag and fuel arrive raw; the commission and net are derived with the same
 *  helper the pro uses (utils/gwiCommission), so the two screens cannot disagree. */
export interface InvoiceListDriverPay {
  recno: number;
  type: string;
  driver: string;
  rate: number | null;
  gwiCommission: string;
  fuelAmount: number;
  payStatus: string;
  eventDate: string;
  payrollDate: string;
  lineItemPro: string;
}

export interface InvoiceListRow {
  pronumber: number;
  invoicerecno: number;
  accessorialnum: number;
  invoicedate: string | null;
  invoicetotal: number;
  creditamt: number;
  netTotal: number;
  masexportdate: string | null;
  invoicecomments: string;
  billtorefno: string;
  donotbillreason: string;
  invoiceDeleted: boolean;
  orderDeleted: boolean;

  /** The quote this invoice was priced from. Either half being 0 means there is nothing
   *  to open — no quote at all (17% of live invoices), or a number whose revision is 0
   *  and so names no revision row. Optional because a cached bundle can outlive a
   *  deploy and meet an API that does not send them yet. */
  quoteNumber?: number;
  revisionNumber?: number;
  /** True when the pair above is the MAIN invoice's, borrowed because this accessorial
   *  carries none of its own — the fallback the estimate engine and the quote-validity
   *  check already apply. Most accessorials are in this state. */
  quoteFromMain?: boolean;

  /** The pro has EDI to show — a live edi_orders row, or an order created from EDI. The
   *  same gate the Addl Info tab's "View EDI Info" link uses. */
  hasEdi?: boolean;

  /** The chassis window and its day count, as the pro's Addl Cost tab shows them. */
  chassisUsage?: string;
  chassisDays?: string;
  /** The calculated chassis fee, and whether any live invoice on the pro bills it. A fee
   *  with nothing billing it is the Addl Cost tab's red case. */
  chassisFeeAmount?: number;
  hasChassisFee?: boolean;

  /** The pro's additional driver pay, carried once per pro (on its first row). */
  driverPay?: InvoiceListDriverPay[];

  masterpro: string;
  container: string;
  chassisid: string;
  size: string;
  bol: string;
  po: string;
  ssline: string;
  vessel: string;
  lbs: string;
  sealnumber: string;
  commodity: string;
  movetype: string;
  dcnumber: string;
  mode: string;
  dept: string;
  comments: string;
  csr: string;
  whsecsr: string;
  dispatcher: string;
  revenueregion: string;
  templatetype: string;
  template: string;
  approvedby: string;
  perdiemstartdate: string | null;
  otr: string;
  createdby: string;
  createdatetime: string | null;
  lastchangedby: string;
  lastchangedatetime: string | null;

  /** V1's Y/N order flag strip, in V1's order, each carrying its own emphasis. */
  flags: InvoiceListFlag[];

  voyage: string;
  equiptype: string;
  kgs: string;
  portoforiginname: string;
  portofdischargename: string;
  priority: string;
  csrname: string;
  whsecsrname: string;

  legs: InvoiceListLeg[];

  origname: string;
  destname: string;
  destcitystatezip: string;
  deliveredAt: string | null;
  origapptnumber: string;

  /** Which required-document flags are set on this invoice. */
  requiredDocs: string[];
  haveTroubleTickets: boolean;
  lineItems: InvoiceListLineItem[];
  /** Every live accessorial on this pro, carried on the BASE row only (accessorialnum
   *  0). Deliberately NOT the same set as the page's rows: it includes accessorials
   *  already invoiced elsewhere, which is the whole reason the strip exists. */
  accessorials: InvoiceListAccessorial[];
}

/** One reason the invoice cannot be raised, in V1's banner wording. */
export interface InvoiceHold {
  code: string;
  label: string;
  detail: string[];
  pronumbers: number[];
  /** WHICH CHARGES this hold lands on. Empty means it could not be attributed and refuses
   *  the whole selection. The pro is not fine enough once a bill-to invoices separately —
   *  a pro carrying two accessorials becomes two invoices. */
  invoicerecnos: number[];
  /** V1 computes this one but never lets it block, so on V1 it can go unseen entirely. */
  v1Silent: boolean;
}

/** A check that could not be run. Not a pass — it refuses whatever it names, and refuses
 *  everything when it names nothing. */
export interface InvoiceSkip {
  reason: string;
  pronumbers: number[];
  invoicerecnos: number[];
}

/** `billtos.invoiceseparately`. */
export type InvoiceSeparately = 'N' | 'ALWAYS' | 'MAIN';

/** Does this hold or skip refuse THIS charge?
 *
 *  The mirror of `holds.Hold.applies_to` on the server, which stays the authority — this
 *  exists so the screen can decide whether to OFFER Create Invoice without paying for a
 *  full review first. Charge-level attribution wins where it exists; the pro is the
 *  fallback and is right for the pro-level checks, because an unapproved order or a
 *  missing POD holds every invoice raised from that pro. Attributed to nothing means it
 *  holds everything: a check that could not say which charge it failed on has cleared
 *  none of them. */
export function holdApplies(
  h: { pronumbers?: number[]; invoicerecnos?: number[] },
  invoicerecno: number,
  pronumber: number,
): boolean {
  // Optional reads, not `.length` straight off: a response without charge attribution
  // must degrade to the pro, not throw. This runs during render, so the difference
  // between tolerant and strict here is a white screen.
  if (h.invoicerecnos?.length) return h.invoicerecnos.includes(invoicerecno);
  if (h.pronumbers?.length) return h.pronumbers.includes(pronumber);
  return true;
}

export interface InvoiceListCandidate {
  masterpro: string;
  billto: number;
  billtoname: string;
  invhistoryrecno: number;
  invoicetype: string;
  deleted: boolean;
  invoicedate: string | null;
  accessorialnum: number | null;
}

export interface InvoiceListTarget {
  masterpro: string;
  billto: number;
  billtoname: string;
  invhistoryrecno: number;
  invoicetype: string;
  /** `<sage tag>-<masterpro><type>`. Empty before the invoice exists. */
  invoicenumber: string;
  /** No invoice_history row yet — these orders could be invoiced but have not been. */
  isNew: boolean;
  invoiceDeleted: boolean;
  mas90code: string;
  billtoAddress: string[];
  billtoCityState: string;
  billtoZipCountry: string;
  credithold: string;
  creditterms: string;
}

export interface InvoiceListResponse {
  status: InvoiceListStatus;
  reason: string;

  target: InvoiceListTarget | null;
  rows: InvoiceListRow[];
  total: number;
  pronumbers: number[];

  onHold: boolean;
  /** False once the invoice exists: the holds gate Create Invoice, so V1 stops running
   *  them. The EDI errors are the exception and always run. */
  holdsEvaluated: boolean;
  holds: InvoiceHold[];
  /** Checks that could not be evaluated. Non-empty means this list is UNDER-validated. */
  skipped: string[];
  /** The same checks, attributed. `skipped` is what the banner renders. */
  skips: InvoiceSkip[];
  /** What a hold refuses: on a grouped bill-to one held charge refuses the single invoice
   *  this selection would become; on a separating one it refuses only itself. */
  invoiceSeparately: InvoiceSeparately;

  ediValidated: boolean;
  ediSkippedReason: string;

  /** The invoice's EDI send state. Null when EDI never handled this invoice. */
  edi: InvoiceEdiSend | null;

  /** The driver pay total, the margin, and the two holds that need them are not in this
   *  response — they cost a rate-engine pass per pro and come from `getMarginHolds`
   *  after the page paints. */
  marginPending: boolean;

  candidates: InvoiceListCandidate[];
  /** Which column separates the candidates: 'billto', 'invoicenum' (they are invoiced
   *  and differ by number) or 'masterpro' (they are not, and differ by order). */
  pickfield: string;
}

/** The row `cms.edi_sentinvoices` holds for this invoice, and the one control V1 offers
 *  for it (lib_invoice.pl:1226). `action` is decided server-side so the page renders an
 *  answer instead of re-deriving V1's branch order from three characters. */
export interface InvoiceEdiSend {
  recno: number;
  sent: string;
  resend: string;
  donotsend: string;
  action: '' | 'resend' | 'cancel_resend' | 'send';
}

/** What V1's DRIVER PAY bar shows, per pro. */
export interface InvoiceListProMargin {
  pronumber: number;
  driverPay: number;
  marginPct: number;
  marginLimit: number;
  underLimit: boolean;
}

/** The rate-engine half of the page, for the same filters it was opened on: the two
 *  holds that need driver pay, and the per-pro numbers the Driver Pay bar shows. */
export interface MarginHoldsResponse {
  status: InvoiceListStatus;
  holds: InvoiceHold[];
  skipped: string[];
  skips: InvoiceSkip[];
  margins: InvoiceListProMargin[];
  /** Every pro on the invoice, in order — the caller's work list when batching. */
  pronumbers: number[];
}

/**
 * How many pros to ask for at a time: a small first batch, then larger ones.
 *
 * THE RAMP IS GONE, because the reason for it is. This used to read [10, 25]: the margin
 * pass cost about a second PER PRO, so one call for a 90-pro invoice meant 38 seconds of
 * empty Driver Pay bars, and a small first batch bought a 4.8s first paint at the price of
 * ~3s of total time.
 *
 * `margin_service` now loads per BATCH rather than per pro — nine queries whichever way,
 * measured on test:
 *
 *     150 pros    1.18s    9 queries      (was ~75s and ~990 queries)
 *     250 pros    1.17s    9 queries
 *     500 pros    1.29s    9 queries
 *
 * Splitting that is pure loss: every extra call re-pays the whole nine-query batch to
 * shave nothing off a wait that is already about a second. One call is now both the
 * fastest total AND the fastest first paint.
 *
 * It is a CAP rather than "all of them" because masterpros get genuinely large — the
 * biggest on test carries 3,590 pros — and `pros=` is a query parameter. 250 keeps it
 * near 2KB, well inside what proxies accept; 500 measured fine at 4KB if that is ever
 * wanted. Anything bigger than the cap still streams in, a batch at a time, with first
 * paint about a second either way.
 *
 * The last entry repeats for as long as there are pros left.
 */
export const MARGIN_BATCH_SCHEDULE = [250];

/** Split the pro list the way `MARGIN_BATCH_SCHEDULE` says to. */
export function marginBatches(
  pros: number[],
  schedule: number[] = MARGIN_BATCH_SCHEDULE,
): number[][] {
  const out: number[][] = [];
  for (let i = 0, n = 0; i < pros.length; n += 1) {
    const size = schedule[Math.min(n, schedule.length - 1)];
    out.push(pros.slice(i, i + size));
    i += size;
  }
  return out;
}

/**
 * The driver pay and margin for some or all of the invoice's pros.
 *
 * Pass `pros` to compute one batch. A BATCH'S HOLDS ARE TRUE, BUT A BATCH'S SILENCE IS
 * NOT: every hold returned applies to the pros asked for, while "no margin problems"
 * means nothing until every batch is in. Callers must track that themselves.
 */
export async function getMarginHolds(
  params: InvoiceListParams,
  pros?: number[],
): Promise<MarginHoldsResponse> {
  const res = await api.get('/invoice-list/margin', {
    params: pros?.length ? { ...params, pros: pros.join(',') } : params,
  });
  return res.data;
}

/** The hold verdict for the invoice MINUS the charges the user removed from it. */
export interface SelectionHoldsResponse {
  status: InvoiceListStatus;
  onHold: boolean;
  holdsEvaluated: boolean;
  holds: InvoiceHold[];
  skipped: string[];
  skips: InvoiceSkip[];
  ediValidated: boolean;
  ediSkippedReason: string;
}

/**
 * Re-ask the holds for what is still ON the invoice.
 *
 * V1 re-renders the whole screen after "Remove from this invoice" and leaves the removed
 * recno out of its WHERE, so every verdict follows the removal for free. This page keeps
 * the removed charge visible and badged, so it has to re-ask — otherwise the banner goes
 * on naming a line item that is no longer being billed.
 *
 * The margin holds are deliberately not re-asked: they are per-PRO, and taking one
 * accessorial off a pro does not change that pro's margin.
 */
export async function getSelectionHolds(
  params: InvoiceListParams,
  exclude: number[],
): Promise<SelectionHoldsResponse> {
  const res = await api.get('/invoice-list/holds', {
    params: exclude.length ? { ...params, exclude: exclude.join(',') } : params,
  });
  return res.data;
}

export async function getInvoiceList(
  params: InvoiceListParams,
): Promise<InvoiceListResponse> {
  const res = await api.get('/invoice-list', { params });
  return res.data;
}

/** The in-app path for a set of filters — what a caller links to. */
export function invoiceListPath(params: InvoiceListParams): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && `${v}` !== '') q.set(k, `${v}`);
  });
  const s = q.toString();
  return s ? `/accounting/invoice-list?${s}` : '/accounting/invoice-list';
}
